/**
 * React Query hooks for Metadata Service operations
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MetadataServiceCreateRequest,
  MetadataServiceListParams,
  getMetadataServiceId,
  metadataServicesApi,
} from '@/lib/api/metadata-services';
import {
  MetadataService,
  MetadataServiceListResponse,
  MetadataServiceUpdateFormData,
} from '../types/metadataservice';

// Query keys factory for consistent cache management
export const metadataServiceKeys = {
  all: ['metadata-services'] as const,
  lists: () => [...metadataServiceKeys.all, 'list'] as const,
  list: (params?: MetadataServiceListParams) => [...metadataServiceKeys.lists(), params] as const,
  details: () => [...metadataServiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...metadataServiceKeys.details(), id] as const,
  versions: () => [...metadataServiceKeys.all, 'versions'] as const,
};

/**
 * Hook to fetch list of metadata services
 */
export function useMetadataServices(params?: MetadataServiceListParams) {
  return useQuery({
    queryKey: metadataServiceKeys.list(params),
    queryFn: async () => {
      const response = await metadataServicesApi.list(params);

      // Handle multiple response formats:
      // 1. { data: { items: [...], total } } - wrapped format
      // 2. { items: [...], total } - direct format
      // 3. [...] - array format
      const responseWithData = response as MetadataServiceListResponse & {
        data?: MetadataServiceListResponse;
      };
      const data = responseWithData.data || response;
      const items = data.items || (data as unknown as MetadataService[]);

      // Ensure we have an array - throw error for React Query to handle
      if (!Array.isArray(items)) {
        throw new Error('Invalid metadata service response format from API');
      }

      // Ensure each metadata service has an id field for UI compatibility
      const servicesWithId = items.map((service) => ({
        ...service,
        id: getMetadataServiceId(service.name),
      }));

      // Return in expected format
      return {
        items: servicesWithId,
        total: data.total || servicesWithId.length,
      };
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single metadata service by name
 */
export function useMetadataService(name: string, options?: { enabled?: boolean }) {
  const id = getMetadataServiceId(name);
  return useQuery({
    queryKey: metadataServiceKeys.detail(id),
    queryFn: () => metadataServicesApi.get(name),
    enabled: options?.enabled ?? !!name,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch available versions for dropdown
 */
export function useMetadataServiceVersions() {
  return useQuery({
    queryKey: metadataServiceKeys.versions(),
    queryFn: async () => {
      const response = await metadataServicesApi.getVersions();
      return response.versions || [];
    },
  });
}

/**
 * Hook to create a new metadata service
 */
export function useCreateMetadataService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MetadataServiceCreateRequest) => metadataServicesApi.create(data),
    onSuccess: () => {
      // Invalidate all metadata service lists to refetch
      queryClient.invalidateQueries({ queryKey: metadataServiceKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing metadata service
 */
export function useUpdateMetadataService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: MetadataServiceUpdateFormData }) => {
      return metadataServicesApi.update(name, data);
    },
    onSuccess: (updatedService, { name }) => {
      const id = getMetadataServiceId(name);
      // Update the specific metadata service in cache
      queryClient.setQueryData(metadataServiceKeys.detail(id), updatedService);
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: metadataServiceKeys.lists() });
    },
  });
}

/**
 * Hook to delete a metadata service
 */
export function useDeleteMetadataService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) => {
      return metadataServicesApi.delete(name);
    },
    onSuccess: (_, { name }) => {
      const id = getMetadataServiceId(name);
      // Remove from cache
      queryClient.removeQueries({ queryKey: metadataServiceKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: metadataServiceKeys.lists() });
    },
  });
}
